/**
 * ambient_look — the ambient life particle layer's LOOK (2026-09-29): the leaf silhouettes, the devil's dust puff, the
 * leaf material (lit, alpha-tested, dissolving through a dithered per-instance fade) and the dust colours. Split out of
 * AtlasAmbientLifeModule so the picture bed (src/probe/ambient_particles_bed.ts) draws with exactly the module's look
 * without importing the game.
 */
import * as THREE from 'three/webgpu';
import { attribute, float, fract, normalViewGeometry, screenCoordinate, select } from 'three/tsl';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';

/** The skirt's puffs are a denser dust than the column's grains — a shade darker, never a dark band. */
export const SKIRT_HEX = 0xd0b184;
/** Sunlit dust: a light tan (a darker sand read as a grey-violet haze against the sky in the bed's picture). */
export const GRAIN_HEX = 0xd8bd92;

let leafTex: THREE.CanvasTexture[] | null = null;
/** Three leaf silhouettes (different shapes and tilts) — white with a real alpha, tinted per leaf by the instance colour. */
export function leafTextures(): THREE.CanvasTexture[] {
  if (leafTex) return leafTex;
  leafTex = [0, 1, 2].map((k) => {
    const cv = document.createElement('canvas');
    cv.width = cv.height = 64;
    const g = cv.getContext('2d')!;
    g.translate(32, 32); g.rotate(-0.6 + k * 1.05);
    const w = [17, 12, 21][k];
    g.fillStyle = 'rgba(255,255,255,1)';
    g.beginPath(); g.moveTo(0, -26); g.quadraticCurveTo(w, -6, 0, 24); g.quadraticCurveTo(-w, -6, 0, -26); g.fill();
    g.strokeStyle = 'rgba(150,150,150,0.9)'; g.lineWidth = 1.6;
    g.beginPath(); g.moveTo(0, -22); g.lineTo(0, 28); g.stroke(); // the midrib + a stalk
    g.lineWidth = 0.9; g.strokeStyle = 'rgba(175,175,175,0.8)';
    for (let v = -14; v <= 10; v += 6) { g.beginPath(); g.moveTo(0, v); g.lineTo(w * 0.55, v - 6); g.moveTo(0, v); g.lineTo(-w * 0.55, v - 6); g.stroke(); } // veins
    const t = new THREE.CanvasTexture(cv);
    t.colorSpace = THREE.SRGBColorSpace;
    t.userData.shared = true; // cached for the page: a re-anchor's teardown must not free it
    return t;
  });
  return leafTex;
}
let dustTex: THREE.CanvasTexture | null = null;
/** A soft, grainy puff of sand (the devil's grains). */
export function dustTexture(): THREE.CanvasTexture {
  if (dustTex) return dustTex;
  const cv = document.createElement('canvas');
  cv.width = cv.height = 64;
  const g = cv.getContext('2d')!;
  const grd = g.createRadialGradient(32, 32, 2, 32, 32, 30);
  grd.addColorStop(0, 'rgba(255,255,255,0.9)'); grd.addColorStop(0.5, 'rgba(255,255,255,0.45)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd; g.fillRect(0, 0, 64, 64);
  let s = 7;
  const r = (): number => { s = (s * 16807) % 2147483647; return s / 2147483647; };
  g.globalCompositeOperation = 'destination-out';
  for (let i = 0; i < 90; i++) { g.fillStyle = `rgba(0,0,0,${0.15 + r() * 0.35})`; g.fillRect(r() * 64, r() * 64, 2, 2); }
  dustTex = new THREE.CanvasTexture(cv);
  dustTex.userData.shared = true;
  return dustTex;
}

/**
 * A leaf silhouette's material: lit, double-sided, alpha-tested at 0.5 — and FADED by a per-instance `aFade` through a
 * screen-space dither (interleaved gradient noise, the house LOD-fade pattern of lib/instancing.ts): a fragment is kept
 * while its noise is under the fade, so a leaf dissolves in and out without ever turning translucent (no sorting) and
 * without losing its crisp alpha-tested edge.
 */
export function leafMaterial(tex: THREE.Texture, k: number): THREE.MeshStandardNodeMaterial {
  const mat = new THREE.MeshStandardNodeMaterial({ map: tex, alphaTest: 0.5, side: THREE.DoubleSide, roughness: 0.7, metalness: 0 });
  mat.name = `ambient-leaf-${k}`;
  const f = attribute('aFade', 'float');
  const sc = screenCoordinate;
  const n = fract(float(52.9829189).mul(fract(sc.x.mul(0.06711056).add(sc.y.mul(0.00583715)))));
  mat.opacityNode = select(n.lessThan(f), float(1), float(0));
  mat.alphaTestNode = float(0.5);
  // TWO-SIDED LIGHT: both faces take the quad's own normal (three negates it on the back face), so a sunlit leaf seen
  // from BELOW is lit like one seen from above — a thin leaf passes the light through. With the flip, every leaf
  // overhead showed its shadowed underside: black slivers against the sky (the bed's picture, 2026-09-29).
  mat.normalNode = normalViewGeometry;
  // the slots are re-dealt every frame (a spawn or a landing shifts them), so three's previous-instance matrices would
  // read a leaf's velocity as the jump to another leaf's slot — a streak under TAA / motion blur. A leaf's own fall
  // (~1 m/s, 10 cm) blurs nothing worth keeping.
  pinStaticMotionVectors(mat);
  return mat;
}
